import { useCallback, useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';

export function useResiAccess() {
    const { accessSummary } = useAuth();

    const scopes = useMemo(() => accessSummary?.all || [], [accessSummary]);

    const getAllowedScopes = useCallback(
        permission => {
            return scopes.filter(scope => scope.permissions.has(permission));
        },
        [scopes]
    );

    const getDeniedScopes = useCallback(
        permission => {
            return scopes.filter(scope => !scope.permissions.has(permission));
        },
        [scopes]
    );

    const can = useCallback(
        permission => {
            return getAllowedScopes(permission).length > 0;
        },
        [getAllowedScopes]
    );

    const cannot = useCallback(
        permission => !can(permission),
        [can]
    );

    const canForProperty = useCallback(
        (permission, propertyId) => {
            const scope = accessSummary?.byPropertyId?.[String(propertyId)];

            return scope?.permissions?.has(permission) === true;
        },
        [accessSummary]
    );

    const canForOrg = useCallback(
        (permission, orgId) => {
            const orgScope = accessSummary?.byOrgId?.[String(orgId)];

            return (
                orgScope?.properties?.some(scope =>
                    scope.permissions.has(permission)
                ) === true
            );
        },
        [accessSummary]
    );

    const getAllowedProperties = useCallback(
        permission => {
            return getAllowedScopes(permission).map(scope => scope.userProperty);
        },
        [getAllowedScopes]
    );

    const getAllowedOrgs = useCallback(
        permission => {
            const orgMap = new Map();

            getAllowedScopes(permission).forEach(scope => {
                if (!orgMap.has(scope.orgId)) {
                    orgMap.set(scope.orgId, scope.org);
                }
            });

            return Array.from(orgMap.values());
        },
        [getAllowedScopes]
    );

    return {
        accessSummary,
        scopes,

        can,
        cannot,

        canForProperty,
        canForOrg,

        getAllowedScopes,
        getDeniedScopes,
        getAllowedProperties,
        getAllowedOrgs
    };
}